import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Grid,
  Paper,
  Button,
  Container,
  Tooltip,
  IconButton,
  FormControlLabel,
  Switch,
  Box,
  Typography,
  InputAdornment,
  styled,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import { useQuery } from 'hooks';
import { debounce } from 'lodash';
import { useConfirm } from 'hooks';
import Title from 'components/Title';
import { Table } from 'components/UI';
import { UserContext } from 'context';
import { toQueryString } from 'utils';
import Layout from 'components/Layout';
import { uiActions } from 'state/modules/UI';
import { useHistory } from 'react-router-dom';
import OrgAbility from 'components/OrgAbility';
import { SearchField } from 'components/FormFields';
import { vehicleActions } from 'state/modules/vehicles';
import { VehicleStatusSelect } from 'components/DataFields';
import { PartnersSelect, VehiclesGroupsSelect } from 'components/AsyncFields';
import {
  BulkShareServicesModal,
  VehicleBulkUploadModal,
  ChangeVehicleStatusModal,
  BulkUnshareServicesModal,
} from 'components/Modals';
import { FETCH_STATE } from 'constants';
import PropTypes from 'prop-types';

// Styled Components
const SearchIconWrapper = styled(InputAdornment)(({ theme }) => ({
  '& svg': {
    color: theme.palette.secondary.light,
    fontSize: theme.typography.pxToRem(14),
  },
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  marginLeft: 0,
  '& .MuiFormControlLabel-label': {
    fontSize: theme.typography.pxToRem(14),
  },
}));

const VehiclesList = () => {
  const urlParams = useQuery();
  const history = useHistory();
  const dispatch = useDispatch();
  const isMounted = useRef(false);
  const abortController = useRef(null);
  const { orgType } = useContext(UserContext);
  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    status_filter: 'exclude',
    statuses: 'RETIRED',
    ...urlParams,
  });
  const [currentVehicle, setCurrentVehicle] = useState(null);
  const partnerField = orgType === 'msOwnerOrg' ? 'service_id' : 'owner_id';
  const [resetPaginationToggle, setResetPaginationToggle] = useState(false);
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [openVehicleBulkUploadModal, setOpenVehicleBulkUploadModal] = useState(false);
  const [openShareModalOpen, setOpenShareModal] = useState(false);
  const [openUnshareModalOpen, setOpenUnshareModal] = useState(false);
  const [openChangeVehiclesStatus, setOpenChangeVehiclesStatus] = useState(false);
  const [clearSelectedRows, setClearSelectedRows] = useState(false);

  const fetchingVehicles = useSelector((state) => state.vehicles?.fetchingVehicles);
  const vehicles = useSelector((state) => state.vehicles?.vehicles);
  const pagination = useSelector((state) => state.vehicles?.pagination);
  const fetchedChangeStatusSuccess = useSelector(
    (state) => state.vehicles?.fetchedChangeStatusSuccess
  );
  const fetchedDeleteVehicleSuccess = useSelector(
    (state) => state.vehicles?.fetchedDeleteVehicleSuccess
  );
  const core = useSelector((state) => state.config?.settings?.core);
  const hasPartnership = useSelector(
    (state) => state.config?.settings?.core?.partnership?.enabled || false
  );
  const loading = fetchingVehicles === FETCH_STATE.LOADING;
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );
  const refetch = fetchedDeleteVehicleSuccess;
  const allowDeleteVehicle = core?.vehicle?.delete_allowed;

  const [toggleDeleteVehicle, DeleteVehicleConfirm] = useConfirm({
    cancelText: i18n.t('common.cancel'),
    confirmText: i18n.t('common.delete'),
    title: i18n.t('vehicle.delete'),
    message: i18n.t('vehicle.delete_message'),
    onConfirm: useCallback(() => {
      dispatch(vehicleActions.fetchDeleteVehicle(currentVehicle));
    }, [dispatch, currentVehicle]),
  });

  const handleDeleteVehicle = (id) => {
    dispatch(vehicleActions.resetDeleteVehicle());
    setCurrentVehicle(id);
    toggleDeleteVehicle();
  };
  const ActionsMenu = ({ row }) => {
    const handleClick = () => handleDeleteVehicle(row?.id);
    return row?.status !== 'RETIRED' ? (
      <Tooltip title={i18n.t('vehicle.delete')}>
        <IconButton
          variant="contained"
          aria-haspopup="true"
          onClick={handleClick}
          aria-controls="card-menu"
          data-cy={`users-list-delete-${row?.id}`}
        >
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    ) : (
      ''
    );
  };

  ActionsMenu.propTypes = {
    row: PropTypes.object.isRequired,
  };

  const columns = [
    {
      name: i18n.t('common.id'),
      selector: (row) => row?.id,
      sortField: 'id',
      sortable: true,
      grow: 0,
      minWidth: '4rem',
    },
    {
      name: i18n.t('common.name'),
      selector: (row) => row?.name,
      sortField: 'name',
      cell: (row) => row?.name || '--',
      sortable: true,
      grow: 2,
    },
    {
      name: i18n.t('common.type'),
      selector: (row) =>
        `${row?.vehicle_type?.make ? `${row?.vehicle_type?.make} ` : ''}${
          row?.vehicle_type?.model || ''
        }` || '--',
      grow: 2,
    },
    {
      name: i18n.t('vehicle.license_plate'),
      selector: (row) => row?.license_plate || '--',
      grow: 2,
    },
    {
      name: i18n.t('vehicle.vin'),
      selector: (row) => row?.vin,
      sortField: 'vin',
      cell: (row) => row?.vin || '--',
      sortable: true,
      grow: 2,
    },
    {
      name: i18n.t('vehicle.service_group'),
      selector: (row) => row?.vehicle_group?.name || row?.customer_vehicle_group?.name || '--',
      grow: 3,
      omit: orgType === 'msOwnerOrg',
    },
    {
      name: sharingType === 'FFB' ? i18n.t('common.partners') : i18n.t('common.services'),
      grow: 2,
      selector: (row) => row?.organization_name,
      omit: ['msServiceOrg', 'plainOrg'].includes(orgType),
      cell: (row) => loadService(row),
    },
    {
      name: i18n.t('common.partner'),
      grow: 2,
      selector: (row) => row?.organization_name,
      omit: ['plainOrg', 'msOwnerOrg'].includes(orgType),
      cell: (row) =>
        row?.vehicle_group?.parent?.organization_name ||
        row?.owner_vehicle_group?.organization.name,
    },
    {
      name: i18n.t('common.fleet'),
      selector: (row) => row?.vehicle_group,
      grow: 3,
      omit: ['msServiceOrg', 'plainOrg'].includes(orgType),
      cell: (row) => row?.vehicle_group?.name || row?.owner_vehicle_group?.name || '--',
    },
    {
      name: i18n.t('common.status'),
      selector: (row) =>
        row?.status ? i18n.t(`status.${row?.status}`, { defaultValue: row?.status }) : '--',
    },
    {
      name: i18n.t('common.actions'),
      cell: (row) => <ActionsMenu row={row} />,
      ignoreRowClick: true,
      allowOverflow: true,
      sortable: false,
      omit: !allowDeleteVehicle,
    },
  ];

  const loadService = (row) => {
    if (row.services_shared) {
      return loadServiceV2(row);
    }
    return loadServiceV1(row);
  };

  const loadServiceV2 = (row) => {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexWrap: 'wrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        onClick={() => handleRowClick(row)}
      >
        {row?.services_shared?.length > 0 ? (
          <>
            {row?.services_shared.map((value, index) => {
              return (
                <Typography key={index} component="span">
                  <Box sx={{ fontSize: 13, mr: 0.25, display: 'inline-block' }}>
                    {value}
                    {index + 1 !== row?.services_shared.length ? ', ' : ''}
                  </Box>
                </Typography>
              );
            })}
          </>
        ) : (
          '--'
        )}
      </Box>
    );
  };

  const loadServiceV1 = (row) => {
    return (
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexWrap: 'wrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        onClick={() => handleRowClick(row)}
      >
        {row?.to_orgs?.length > 0 ? (
          <>
            {row?.to_orgs
              .sort((a, b) => b.is_owner - a.is_owner)
              .map((value, index) => {
                if (value?.is_owner) return;
                return (
                  <Typography key={index} component="span">
                    <Box sx={{ fontSize: 13, mr: 0.25, display: 'inline-block' }}>
                      {value?.organization?.name}
                      {index + 1 !== row?.to_orgs.length ? ', ' : ''}
                    </Box>
                  </Typography>
                );
              })}
          </>
        ) : (
          '--'
        )}
      </Box>
    );
  };

  const fetchVehicles = useCallback(
    (params) => {
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      let queryFields = `{id name display_name license_plate status vin services_shared vehicle_type{make model} owner_vehicle_group{name organization{name}} customer_vehicle_group}`;
      dispatch(
        vehicleActions.fetchVehicles(
          params,
          { signal: abortController?.current?.signal },
          queryFields
        )
      );
    },
    [dispatch]
  );

  const fetchVehiclesDebounced = useCallback(
    debounce((params) => fetchVehicles(params), 1000),
    [fetchVehicles]
  );

  useEffect(() => {
    if (fetchedChangeStatusSuccess) {
      setSelectedVehicles([]);
      setClearSelectedRows(false);
    }
  }, [fetchedChangeStatusSuccess]);

  // Cycle trough ClearSelectedRows to clear the selected vehicles
  // Set to true on search to trigger material-ui-table clean up
  useEffect(() => {
    if (fetchingVehicles === FETCH_STATE.DONE) {
      setSelectedVehicles([]);
      setClearSelectedRows(false);
    }
  }, [fetchingVehicles]);

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current) {
      fetchVehiclesDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchVehiclesDebounced, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchVehicles(params);
  }, []);

  // On refetch delete/update success no debounce
  useEffect(() => {
    if (refetch) {
      fetchVehicles(params);
    }
  }, [fetchVehicles, refetch]);

  useEffect(() => {
    dispatch(vehicleActions.resetOnboarding());
  }, [dispatch]);

  const handleParams = (params) => {
    const data = { ...params };
    setParams(data);
    const _params = toQueryString(data);
    history.replace(buildUrl(`/vehicles${_params}`));
  };

  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };
  // sort filter
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      page: pagination?.currentPage,
      sort_field: column.sortField,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    handleParams(newParams);
    setResetPaginationToggle(!resetPaginationToggle);
  };
  // change number of items per page
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };
  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    history.push(buildUrl(`/vehicles/${row?.id}`));
  };
  // search by status, keyword and organization

  const valueResolver = (name, value) => {
    switch (name) {
      case 'organization_id':
        return value.id;
      case 'status_filter':
        return value ? undefined : 'exclude';
      default:
        return value;
    }
  };

  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      [name]: valueResolver(name, value),
    };

    if (name === 'statuses') {
      filters.status_filter = 'only';
    } else if (['service_id', 'owner_id'].includes(name)) {
      filters.group_id = '';
    }

    if (name === 'status_filter') {
      if (value) {
        delete filters.statuses;
      } else {
        filters.statuses = 'RETIRED';
      }
    }

    // remove empty fields
    Object.keys(filters).forEach((key) => !filters[key] && delete filters[key]);
    handleParams(filters);
    setSelectedVehicles([]);
    setClearSelectedRows(true);
  };

  /**
   * Handle selected row
   * @param {object} row Data of the row selected
   */
  const handleSelected = (state) => {
    setSelectedVehicles(state.selectedRows);
  };

  const handleModalOpen = () => {
    dispatch(vehicleActions.resetChangeStatus());
    setOpenChangeVehiclesStatus((prevState) => !prevState);
  };

  const handleShareModalOpen = () => {
    const hasOfflineVehicles = selectedVehicles.filter((e) => e.status !== 'ACTIVE') || false;

    if (hasOfflineVehicles?.length > 0) {
      return dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          autoHideDuration: null,
          message: i18n.t('vehicle.share_offline_message'),
        })
      );
    }
    setOpenShareModal((prevState) => !prevState);
  };

  const handleUnshareModalOpen = () => {
    setOpenUnshareModal((prevState) => !prevState);
  };

  const handleCsvModal = () => {
    setOpenVehicleBulkUploadModal((prevState) => !prevState);
  };

  return (
    <Layout title={i18n.t('sidebar.vehicles')}>
      <Container maxWidth={false} disableGutters>
        <Grid container spacing={2} sx={{ m: 0, width: '100%' }}>
          <Grid size={{ xs: 12 }}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Grid>
                <Title title={i18n.t('sidebar.vehicles')} />
              </Grid>
              <Grid>
                <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                  {orgType !== 'msServiceOrg' ? (
                    <>
                      <Button
                        onClick={handleModalOpen}
                        variant="outlined"
                        sx={{
                          textTransform: 'capitalize',
                          mr: 2,
                        }}
                        color="primary"
                        disabled={!selectedVehicles.length}
                        data-cy="change-vehicles-status-button"
                      >
                        {i18n.t('common.update_status')}
                      </Button>
                      <Button
                        sx={{
                          textTransform: 'capitalize',
                          mr: 2,
                        }}
                        onClick={handleCsvModal}
                        variant="outlined"
                        color="primary"
                      >
                        {i18n.t('common.import_csv')}
                      </Button>
                      <Button
                        color="primary"
                        variant="contained"
                        data-cy="create-vehicle-button"
                        sx={{ textTransform: 'capitalize' }}
                        onClick={() => history.push(buildUrl('/vehicles/create'))}
                      >
                        {i18n.t('vehicle.create')}
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={handleModalOpen}
                      variant="contained"
                      sx={{
                        textTransform: 'capitalize',
                      }}
                      color="primary"
                      disabled={!selectedVehicles.length}
                      data-cy="change-vehicles-status-button"
                    >
                      {i18n.t('common.update_status')}
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography sx={{ maxWidth: '30rem', fontSize: '0.875rem' }}>
              {i18n.t('vehicle.description')}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ pb: 3.125 }}>
            <Grid
              container
              direction="row"
              justifyContent="flex-start"
              alignItems="flex-end"
              spacing={2}
            >
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <SearchField
                  margin="none"
                  label={i18n.t('vehicle.serch_vehicle')}
                  field={{
                    name: 'keyword',
                    onChange: search,
                    value: params.keyword,
                  }}
                  inputProps={{
                    startAdornment: (
                      <SearchIconWrapper position="start">
                        <SearchIcon />
                      </SearchIconWrapper>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <VehicleStatusSelect
                  margin="none"
                  form={{ setFieldValue: search }}
                  label={i18n.t('vehicle.filter_by_status')}
                  field={{
                    name: 'statuses',
                    status_filter: 'only',
                    value: params.statuses === 'RETIRED' ? '' : params.statuses,
                  }}
                  omitFields={['RETIRED']}
                />
              </Grid>
              {hasPartnership && (
                <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                  <PartnersSelect
                    margin="none"
                    form={{ setFieldValue: search }}
                    label={i18n.t(
                      `vehicle.${sharingType === 'FFB' ? 'filter_by_partner' : 'filter_by_service'}`
                    )}
                    field={{
                      name: partnerField,
                      value: params[partnerField],
                    }}
                    customParams={{ status: ['ACTIVE', 'CANREQUESTED'] }}
                  />
                </Grid>
              )}
              <Grid size={{ xs: 12, sm: 4, md: 3 }}>
                <StyledFormControlLabel
                  value="start"
                  control={
                    <Switch
                      name="active"
                      color="primary"
                      onChange={(_, value) => search('status_filter', value)}
                      checked={
                        params?.status_filter !== 'exclude' && params?.status_filter !== 'only'
                      }
                    />
                  }
                  label={i18n.t('vehicle.include_retired')}
                  labelPlacement="start"
                />
              </Grid>
            </Grid>
          </Grid>
          {hasPartnership && (
            <OrgAbility subject={['msOwnerOrg']}>
              <Grid size={{ xs: 12 }}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12 }}>
                    <Typography variant="h3">
                      <Box sx={{ fontSize: 16, fontWeight: 700 }}>
                        {sharingType === 'FFB'
                          ? i18n.t('vehicle.share_n_unshare_to_partners')
                          : i18n.t('vehicle.share_n_unshare')}
                      </Box>
                    </Typography>
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Box sx={{ maxWidth: 600 }}>
                      <Typography variant="h3">
                        <Box sx={{ fontSize: 14, fontWeight: 400, mt: 1 }}>
                          {sharingType === 'FFB'
                            ? i18n.t('vehicle.share_n_unshare_description_partners')
                            : i18n.t('vehicle.share_n_unshare_description')}
                        </Box>
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
            </OrgAbility>
          )}
          <Grid size={{ xs: 12 }} sx={{ pb: 3.125 }}>
            <Grid
              container
              spacing={2}
              direction="row"
              alignItems="flex-end"
              justifyContent="flex-start"
            >
              <Grid size={{ xs: 6, sm: 4, md: 3 }}>
                <VehiclesGroupsSelect
                  margin="none"
                  form={{ setFieldValue: search }}
                  customParams={{
                    group_type: orgType === 'msOwnerOrg' ? 'OWNER' : 'CUSTOMER',
                  }}
                  field={{
                    name: 'group_id',
                    value: params.group_id || '',
                  }}
                  type="vgList"
                  label={i18n.t(
                    ['msServiceOrg', 'plainOrg'].includes(orgType)
                      ? 'vehicle.vehicle_group'
                      : 'vehicle.select_fleet'
                  )}
                />
              </Grid>
              {hasPartnership && (
                <OrgAbility subject={['msOwnerOrg']}>
                  <>
                    <Grid>
                      <Button
                        size="small"
                        color="primary"
                        variant="outlined"
                        data-cy="share-button"
                        onClick={handleShareModalOpen}
                        sx={{ textTransform: 'capitalize' }}
                        disabled={!selectedVehicles.length || !params.group_id}
                      >
                        {i18n.t('common.share')}
                      </Button>
                    </Grid>
                    <Grid>
                      <Button
                        size="small"
                        color="primary"
                        variant="outlined"
                        data-cy="unshare-button"
                        onClick={handleUnshareModalOpen}
                        sx={{ textTransform: 'capitalize' }}
                        disabled={!selectedVehicles.length || !params.group_id}
                      >
                        {i18n.t('common.unshare')}
                      </Button>
                    </Grid>
                  </>
                </OrgAbility>
              )}
            </Grid>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Paper elevation={2} sx={{ boxShadow: '0px 3px 6px #0000001A' }}>
              <Table
                data={vehicles}
                columns={columns}
                page={params.page}
                onSort={handleSort}
                limit={params.limit}
                selectableRows
                loading={loading}
                handleRowClicked={handleRowClick}
                handlePageChange={handlePageChange}
                defaultSortAsc={!params?.sort_desc}
                defaultSortField={params?.sort_field}
                clearSelectedRows={clearSelectedRows}
                totalRows={pagination?.totalRows || 0}
                handleSelectedRowsChange={handleSelected}
                handlePerRowsChange={handlePerRowsChange}
                paginationDefaultPage={pagination?.currentPage}
                paginationResetDefaultPage={resetPaginationToggle}
              />
            </Paper>
          </Grid>
          {openChangeVehiclesStatus && (
            <ChangeVehicleStatusModal
              handleClick={handleModalOpen}
              open={openChangeVehiclesStatus}
              vehicleIds={selectedVehicles.map((e) => e.id)}
              title={i18n.t('vehicle.change_vehicles_status')}
            />
          )}
          {openVehicleBulkUploadModal && (
            <VehicleBulkUploadModal
              open={openVehicleBulkUploadModal}
              handleClick={handleCsvModal}
              title={i18n.t('vehicle.change_vehicles_status')}
            />
          )}
          {openShareModalOpen && (
            <BulkShareServicesModal
              open={openShareModalOpen}
              vehicleGroupId={params.group_id}
              handleClick={handleShareModalOpen}
              vehicleIds={selectedVehicles.map((e) => e.id)}
              title={i18n.t('vehicle.change_vehicles_status')}
            />
          )}
          {openUnshareModalOpen && (
            <BulkUnshareServicesModal
              open={openUnshareModalOpen}
              vehicleGroupId={params.group_id}
              handleClick={handleUnshareModalOpen}
              vehicleIds={selectedVehicles.map((e) => e.id)}
              title={i18n.t('vehicle.change_vehicles_status')}
            />
          )}
        </Grid>
        <DeleteVehicleConfirm />
      </Container>
    </Layout>
  );
};

export default VehiclesList;
